import type { DoctorDTO } from '@/services';

const HOUR_MS = 1000 * 60 * 60;

function isPendingApprovalDoctor(doctor: Pick<DoctorDTO, 'isApproved' | 'status'>): boolean {
  return !doctor.isApproved && doctor.status !== 'REJECTED' && doctor.status !== 'APPROVED';
}

function isSameLocalDay(date: Date, reference: Date): boolean {
  return (
    date.getFullYear() === reference.getFullYear() &&
    date.getMonth() === reference.getMonth() &&
    date.getDate() === reference.getDate()
  );
}

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function countPendingApprovalDoctors(doctors: DoctorDTO[]): number {
  return doctors.filter(isPendingApprovalDoctor).length;
}

export function countNewPendingApprovalDoctorsToday(
  doctors: DoctorDTO[],
  now = new Date(),
): number {
  return doctors.filter((doctor) => {
    const createdAt = parseDate(doctor.createdAt);
    return !!createdAt && isPendingApprovalDoctor(doctor) && isSameLocalDay(createdAt, now);
  }).length;
}

export function formatAverageApprovalReviewTime(doctors: DoctorDTO[]): string {
  const approvedReviewHours = doctors
    .filter((doctor) => doctor.isApproved || doctor.status === 'APPROVED')
    .map((doctor) => {
      const createdAt = parseDate(doctor.createdAt);
      const updatedAt = parseDate(doctor.updatedAt);
      if (!createdAt || !updatedAt || updatedAt < createdAt) return null;
      return (updatedAt.getTime() - createdAt.getTime()) / HOUR_MS;
    })
    .filter((hours): hours is number => hours !== null);

  if (approvedReviewHours.length === 0) return '0h';

  const avgHours =
    approvedReviewHours.reduce((sum, hours) => sum + hours, 0) / approvedReviewHours.length;

  if (avgHours < 1) return '<1h';
  if (avgHours < 24) return `${Math.round(avgHours)}h`;

  const avgDays = avgHours / 24;
  return `${avgDays.toFixed(avgDays >= 10 ? 0 : 1)}d`;
}
